<template>
  <div class="date-grid">
    <!-- Названия дней недели -->
    <div
      v-for="day in weekdays"
      :key="day"
      class="date-grid__cell date-grid__cell--header"
    >
      {{ day }}
    </div>

    <!-- Список дней -->
    <div
      v-for="(cell, index) in cells"
      :key="index"
      class="date-grid__cell"
      :class="{
        'date-grid__cell--adjacent': !cell.current,
        'date-grid__cell--in-range': hasRange && isInRange(cell.date) && !isBoundary(cell.date),
        'date-grid__cell--today': isActive(cell.date) ? false : isToday(cell.date),
        'date-grid__cell--disabled': cell.disabled
      }"
      @click="cell.disabled ? null : onCellClick(cell)"
    >
      <!-- Для выбранной/активной даты -->
      <template v-if="isActive(cell.date)">
        <!-- Если вход в диапазон: прямоугольный фон -->
        <template v-if="hasRange">
          <div
            class="date-grid__cell--active-bg"
            :class="{
              'date-grid__cell--active-bg--start': isRangeStart(cell.date),
              'date-grid__cell--active-bg--end': isRangeEnd(cell.date)
            }"
          />
        </template>
        <!-- Дата в синем круге для конечных дат -->
        <div class="date-grid__cell--active-circle">
          {{ cell.day }}
        </div>
      </template>
      <!-- Обычная ячейка -->
      <template v-else>
        {{ cell.day }}
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  year: { type: Number, required: true },
  month: { type: Number, required: true },
  /** Для диапазона: [start, end]; для single: Date */
  selectedDate: {
    type: [Array, Date, null],
    required: true
  },
  /** Файл локализации */
  localeMessages: {
    type: Object,
    required: true
  },
  /** До какого дня разрешено выбирать дату */
  maxDate: {
    type: [Date, null],
    default: null
  }
});
const emit = defineEmits(["select-date"]);

const weekdays = computed(() => props.localeMessages.weekdays.short);

function isSameDay(a, b) {
  return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
  );
}

// Проверка сегодняшней даты
const today = new Date();
function isToday(date) {
  return isSameDay(date, today);
}

// Проверяем, в режиме range и обе даты заданы
const hasRange = computed(
    () => Array.isArray(props.selectedDate) && props.selectedDate[0] != null && props.selectedDate[1] != null
);

// Вычисляем хронологические границы диапазона
const rangeBounds = computed(() => {
  if (hasRange.value) {
    const [d1, d2] = props.selectedDate;
    return d1 < d2 ? { start: d1, end: d2 } : { start: d2, end: d1 };
  }
  return null;
});

// Находится ли дата в указанном диапазоне
function isInRange(date) {
  const bounds = rangeBounds.value;
  return bounds ? date >= bounds.start && date <= bounds.end : false;
}

// Является ли дата одной из границ диапазона
function isBoundary(date) {
  const bounds = rangeBounds.value;
  return bounds ? isSameDay(date, bounds.start) || isSameDay(date, bounds.end) : false;
}

// Для закругления: является ли дата начальной
function isRangeStart(date) {
  const bounds = rangeBounds.value;
  return bounds ? isSameDay(date, bounds.start) : false;
}
// Для закругления: является ли дата конечной
function isRangeEnd(date) {
  const bounds = rangeBounds.value;
  return bounds ? isSameDay(date, bounds.end) : false;
}

// Активна ли дата
function isActive(date) {
  if (Array.isArray(props.selectedDate)) {
    return (
        props.selectedDate[0] != null && isSameDay(date, props.selectedDate[0]) ||
        props.selectedDate[1] != null && isSameDay(date, props.selectedDate[1])
    );
  }
  return props.selectedDate instanceof Date && isSameDay(date, props.selectedDate);
}

// Генерация ячеек календаря
const daysInMonth = computed(() => new Date(props.year, props.month + 1, 0).getDate());
const daysInPrevMonth = computed(() => new Date(props.year, props.month, 0).getDate());
const firstDayIndex = computed(() => {
  const d = new Date(props.year, props.month, 1).getDay();
  return (d + 6) % 7;
});

const cells = computed(() => {
  const arr = [];
  // дни предыдущего месяца
  for (let i = firstDayIndex.value; i > 0; i--) {
    const num = daysInPrevMonth.value - i + 1;
    const date = new Date(props.year, props.month - 1, num);
    date.setHours(0, 0, 0, 0);
    arr.push({ day: num, current: false, date, disabled: props.maxDate ? date > props.maxDate : false });
  }
  // дни текущего месяца
  for (let d = 1; d <= daysInMonth.value; d++) {
    const date = new Date(props.year, props.month, d);
    date.setHours(0, 0, 0, 0);
    arr.push({ day: d, current: true, date, disabled: props.maxDate ? date > props.maxDate : false });
  }
  // дни следующего месяца
  const rem = arr.length % 7;
  if (rem) {
    for (let i = 1; i <= 7 - rem; i++) {
      const date = new Date(props.year, props.month + 1, i);
      date.setHours(0, 0, 0, 0);
      arr.push({ day: i, current: false, date, disabled: props.maxDate ? date > props.maxDate : false });
    }
  }
  return arr;
});

function onCellClick(cell) {
  emit("select-date", cell.date);
}
</script>

<style scoped lang="scss">
.date-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-row-gap: 4px;
  user-select: none;
}

.date-grid__cell {
  position: relative;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #373d3f;
  cursor: pointer;
  border-radius: 50%;
  box-sizing: border-box;


  &:hover {
    background: #E0F0FF;
  }
}

.date-grid__cell--header {
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #898989;
  cursor: default;
}

.date-grid__cell--today {
  border: 1px solid #1F9AFF;
}

.date-grid__cell--disabled {
  color: #cacaca;
  cursor: default;
}

.date-grid__cell--adjacent {
  color: #cacaca;
}
.date-grid__cell--in-range {
  background: #e5f2ff;
  border-radius: 0;
}
.date-grid__cell--active-bg {
  position: absolute;
  inset: 0;
  background: #e5f2ff;
}
.date-grid__cell--active-bg--start {
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
}
.date-grid__cell--active-bg--end {
  border-top-right-radius: 50%;
  border-bottom-right-radius: 50%;
}
.date-grid__cell--active-circle {
  position: relative;
  width: 30px;
  height: 30px;
  background: #1f9aff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
</style>